import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Avatar, Button, Card, CheckIcon, EcoPanel, Heading, LeafIcon } from '@openroad/ui';
import { api, formatCo2, formatDay, formatMoney, formatTime, unwrap } from '../lib/api';

/**
 * "You're booked. Ka pai!"
 *
 * The figure in the green panel is `co2SavedGrams × seats` — the whole booking,
 * not one person. The API's field is per person, because that is what a member
 * saves and what the eco dashboard adds up; here the member is being told what
 * their booking kept off the road, which includes the seats they booked for
 * anybody travelling with them.
 */
export function BookingConfirmedScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const booking = useQuery({
    queryKey: ['booking', id],
    queryFn: () => unwrap(api.GET('/bookings/{id}', { params: { path: { id } } })),
    enabled: id !== '',
  });

  const payments = useQuery({
    queryKey: ['payments', 'config'],
    queryFn: () => unwrap(api.GET('/payments/config')),
  });

  /**
   * Send the browser to Stripe's own checkout page.
   *
   * A full navigation rather than an embedded form: card details never touch
   * this application, which is also why there is no Stripe.js here. Stripe
   * returns to this same screen, and what it says then comes from the booking's
   * payment status — set by the webhook — never from the query string it was
   * sent back with.
   */
  const checkout = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/bookings/{id}/checkout-session', { params: { path: { id } } })),
    onSuccess: (session) => window.location.assign(session.checkoutUrl),
  });

  const message = useMutation({
    mutationFn: (tripId: string) => unwrap(api.POST('/threads', { body: { tripId } })),
    onSuccess: (result) => navigate(`/messages/${result.thread.id}`),
  });

  if (booking.isPending) {
    return (
      <main className="mx-auto max-w-[560px] px-6 py-14">
        <p className="text-body text-muted">Loading your booking…</p>
      </main>
    );
  }

  if (booking.isError || !booking.data) {
    return (
      <main className="mx-auto max-w-[560px] px-6 py-14">
        <Card radius="panel" className="p-7 text-center">
          <Heading level="panel" className="mb-2">
            We could not load that booking
          </Heading>
          <p className="m-0 mb-6 text-base text-muted">
            It may have been cancelled. Your trips list has everything you have booked.
          </p>
          <Button variant="cta" onClick={() => navigate('/trips')}>
            View my trips
          </Button>
        </Card>
      </main>
    );
  }

  const it = booking.data;
  const driver = it.trip.driver;
  const awaitingDriver = it.status === 'PENDING_APPROVAL';

  // Only when there is genuinely something to pay: payment configured, the
  // booking not already settled, and the driver has actually accepted it — a
  // request still waiting on them is not payable, because being declined after
  // paying would leave the passenger charged for a refused seat.
  //
  // FAILED counts as owing: a card that was declined is a reason to offer the
  // button again, not to take it away. Leaving it out stranded the passenger on
  // a screen with an unpaid seat and no way to pay for it, while the server was
  // still perfectly willing to open another checkout.
  const owesPayment =
    (payments.data?.enabled ?? false) &&
    (it.paymentStatus === 'REQUIRES_PAYMENT' || it.paymentStatus === 'FAILED') &&
    it.status === 'CONFIRMED';
  const paymentFailed = it.paymentStatus === 'FAILED';
  const paid = it.paymentStatus === 'PAID';
  const abandonedCheckout = search.get('checkout') === 'cancelled';

  return (
    <main className="mx-auto max-w-[560px] px-6 pt-12 pb-16 text-center">
      <div className="mx-auto mb-[22px] flex h-[84px] w-[84px] animate-pop items-center justify-center rounded-full bg-success-tint">
        <CheckIcon size={42} strokeWidth={2.2} className="text-success" />
      </div>

      <Heading level="panel" className="mb-2 text-display-sm tracking-display">
        {awaitingDriver ? 'Request sent. Ka pai!' : "You're booked. Ka pai!"}
      </Heading>
      <p className="mx-auto mb-7 text-base text-muted">
        {awaitingDriver
          ? `${driver.displayName} will let you know shortly whether they can take you.`
          : `Your seat with ${driver.displayName} is confirmed. They'll be in touch before departure.`}
      </p>

      <EcoPanel className="mb-[18px] p-6 text-center">
        <div className="flex items-center justify-center gap-2">
          <LeafIcon size={22} strokeWidth={1.8} className="text-ngahere-fore" />
          <span className="text-caption font-semibold tracking-caps text-ngahere-fore uppercase">
            CO₂ saved on this trip
          </span>
        </div>
        <div className="my-2 font-display text-stat-xl font-bold">
          {formatCo2(it.co2SavedGrams * it.seats)}
        </div>
        <div className="text-meta text-ngahere-body">
          That&rsquo;s a real car kept off the open road. Ngā mihi.
        </div>
      </EcoPanel>

      <Card className="mb-6 p-[18px] text-left">
        <div className="flex items-center gap-3">
          <Avatar
            initials={driver.initials}
            colour={driver.avatarColour}
            size={46}
            aria-hidden="true"
          />
          <div className="flex-1">
            <div className="text-base font-bold">
              {it.trip.origin.name} → {it.trip.destination.name}
            </div>
            <div className="text-meta text-muted">
              {formatDay(it.trip.departureAt)} · {formatTime(it.trip.departureAt)} · {it.seats}{' '}
              {it.seats === 1 ? 'seat' : 'seats'}
            </div>
          </div>
          <div className="font-display text-title font-extrabold text-pohutukawa">
            {formatMoney(it.price.total)}
          </div>
        </div>
      </Card>

      {owesPayment ? (
        <Card className="mb-6 p-[18px] text-left">
          <div className="mb-3 flex items-baseline justify-between">
            <span className="text-base font-semibold">
              {paymentFailed
                ? 'That payment did not go through'
                : abandonedCheckout
                  ? 'Payment not completed'
                  : 'Still to pay'}
            </span>
            <span className="font-display text-title font-extrabold">
              {formatMoney(it.price.total)}
            </span>
          </div>
          <Button
            variant="cta"
            size="hero"
            block
            onClick={() => checkout.mutate()}
            disabled={checkout.isPending}
          >
            {checkout.isPending
              ? 'Opening checkout…'
              : paymentFailed
                ? 'Try paying again'
                : 'Pay for this seat'}
          </Button>
          <p className="mt-2 mb-0 text-center text-mini text-subtle">
            You will be taken to Stripe to pay. Your card details never reach OpenRoad.
          </p>
          {checkout.isError ? (
            <p className="mt-2 mb-0 text-center text-mini text-danger" role="alert">
              {(checkout.error as { message?: string })?.message ??
                'Checkout could not be opened. Try again.'}
            </p>
          ) : null}
        </Card>
      ) : paid ? (
        <p className="mb-6 text-meta text-muted">Paid in full. Ngā mihi.</p>
      ) : null}

      <div className="flex gap-2.5">
        <Button
          variant="outline"
          className="h-12 flex-1"
          onClick={() => message.mutate(it.trip.id)}
          disabled={message.isPending}
        >
          Message driver
        </Button>
        <Button variant="cta" className="h-12 flex-1" onClick={() => navigate('/trips')}>
          View my trips
        </Button>
      </div>

      <button
        type="button"
        onClick={() => navigate('/')}
        className="mt-[18px] cursor-pointer border-none bg-transparent text-meta text-muted underline-offset-2 hover:underline"
      >
        Back to home
      </button>
    </main>
  );
}
